import { createClient, type SupabaseClient } from "@supabase/supabase-js";

/**
 * Browser Supabase client — AUTH ONLY, and only when both VITE_SUPABASE_* vars
 * are set. Unset (local dev / a fully open deployment) the player runs without
 * a login, exactly as before — the ParentGate becomes a pass-through.
 *
 * This gates ACCESS to the app for invited families (magic link, no password).
 * It does not touch content loading or telemetry: the story bank stays static
 * pre-authored JSON on the CDN, and no account data ever enters telemetry
 * events (the child's first name from the URL remains the only PII collected).
 */

const url = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Half a config would FAIL OPEN (gate silently off) — make that loud.
if (Boolean(url) !== Boolean(anon)) {
  console.warn(
    "[tella] Only one of VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY is set — " +
      "the parent gate is DISABLED. Set both (or neither, for an open build)."
  );
}

export const authEnabled = Boolean(url && anon);

export const supabase: SupabaseClient | null = authEnabled
  ? createClient(url as string, anon as string)
  : null;

/**
 * Roles this account may enter, from ADMIN-set app metadata (families are
 * granted "player" when invited; studio designers carry "studio"). Tolerant of
 * missing/garbage shapes — no roles is simply an empty list.
 */
export function tellaRoles(user: { app_metadata?: Record<string, unknown> | null }): string[] {
  const raw = user.app_metadata?.tella_roles;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is string => typeof r === "string")
    .map((r) => r.trim().toLowerCase())
    .filter(Boolean);
}
